// No imports: this file is also a frontend entry point
// (`@borradh-workspace/features/videos/errors`), like graphics/errors.
/**
 * Stable, user-reportable codes for making a video in the Gallery wizard
 * (Marketing → Gallery → Generate → video). The owner sees one standard
 * message plus the code; a support report quoting the code says what broke.
 *
 * Keep codes backwards compatible: owners quote them, and logs and PostHog are
 * searched by them. Same shape as GFX-xxxx (graphics/graphic-generation-errors.ts):
 * 1xxx = AI copy, 2xxx = footage and setup, 3xxx = our save/queue,
 * 4xxx = the render itself, 9000 = not recognised.
 *
 * Where each code is logged: every failed Generate logs one browser warning
 * `video.generate` ("Video not generated (VID-xxxx)") with `errorCode`, `step`
 * and the API `requestId`. The API also tags its own row with `errorCode`:
 * `videos.queueVideoExport.rejected` (warn) for 2xxx, `videos.queueVideoExport`
 * (error) for VID-3002. The worker tags `video-worker.processJob` and the
 * `video_render_failed` event with VID-4001.
 *
 * | Code     | Cause                                              | What to check |
 * | -------- | -------------------------------------------------- | ------------- |
 * | VID-1001 | The AI couldn't write the on-screen copy.          | `videos.generateOrganicCopy` errors at that time ("AI extraction failed: …"): model outage, schema miss. Retry usually works. |
 * | VID-2001 | No footage: the draft has no clips and stock footage found none for the service. | Does the service have uploaded clips? Does it have stock matches (`service_stock_clip`)? A before/after template never takes stock. |
 * | VID-2002 | A chosen clip is still processing.                 | The clip assets' `transcode_status` is `pending`. Retry in a minute. |
 * | VID-2003 | A chosen clip failed to process.                   | The clip assets' `transcode_status` is `failed` (`failedAssetIds` on the log). Replace or re-upload it. |
 * | VID-2004 | A chosen clip no longer exists.                    | `unavailableAssetIds` on the log: deleted, or not this org's asset. Also a render whose clip files have all gone from storage (`video-worker.processJob`). |
 * | VID-2005 | The draft is missing something else it needs (script, voice, recording, on-screen copy). | `configError` on the log, and the video row's `draft_config`. |
 * | VID-2006 | The video was already queued or rendered.          | The video row's `status`; usually a double press. Check the Gallery for the video. |
 * | VID-3001 | The draft video couldn't be saved.                 | `POST /videos` or `PUT /videos/:id` for the logged `requestId` in Better Stack. |
 * | VID-3002 | The render couldn't be queued.                     | `videos.queueVideoExport` errors: Redis/BullMQ reachability for the API. |
 * | VID-4001 | The render started but failed.                     | `video-worker.processJob` and `video_render_failed` by `videoId`; `stalled` in `video-worker` warns means the worker was killed mid-render. |
 * | VID-9000 | Not recognised.                                    | The `video.generate` warning: `step`, `apiStatus`, `requestId`. Give it its own code if it recurs. |
 */
export const VideoGenerationErrorCodes = {
    COPY_FAILED: 'VID-1001',
    NO_FOOTAGE: 'VID-2001',
    CLIPS_PROCESSING: 'VID-2002',
    CLIPS_FAILED: 'VID-2003',
    CLIPS_UNAVAILABLE: 'VID-2004',
    SETUP_INCOMPLETE: 'VID-2005',
    ALREADY_QUEUED: 'VID-2006',
    SAVE_FAILED: 'VID-3001',
    QUEUE_FAILED: 'VID-3002',
    RENDER_FAILED: 'VID-4001',
    UNEXPECTED: 'VID-9000',
};
const KNOWN_CODES = new Set(Object.values(VideoGenerationErrorCodes));
export function isVideoGenerationErrorCode(value) {
    return typeof value === 'string' && KNOWN_CODES.has(value);
}
/** What a failed video says when the files of all its clips have gone. */
export const CLIPS_UNAVAILABLE_MESSAGE = 'The clips in this video are no longer available. Choose different clips and try again.';
/** The support code for a failed render, read from what the video row says. */
export function renderFailureCode(errorMessage) {
    return errorMessage === CLIPS_UNAVAILABLE_MESSAGE
        ? VideoGenerationErrorCodes.CLIPS_UNAVAILABLE
        : VideoGenerationErrorCodes.RENDER_FAILED;
}
/** Fallback message when the failure has no specific recovery guidance. */
export const VIDEO_GENERATION_FAILED_MESSAGE = "Couldn't make your video. Please try again.";
//# sourceMappingURL=video-generation-errors.js.map